/**
 * ActivityWidget Component
 *
 * Виджет для отображения истории изменений организации.
 * Показывает все события и действия с карточкой организации.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Text,
  Stack,
  Box,
  Loader,
  Center,
  Avatar,
  Group,
  Badge,
  ScrollArea,
} from '@mantine/core';
import {
  IconHistory,
  IconPlus,
  IconPencil,
  IconTrash,
  IconUsers,
  IconCalendar,
  IconPhone,
  IconArrowRight,
  IconRobot,
  IconRefresh,
  IconUserPlus,
  IconBuilding,
  IconBuildingCommunity,
  IconAlertTriangle,
} from '@tabler/icons-react';
import BaseWidget from './BaseWidget';
import tokens from '../../../theme/designTokens';

/**
 * API функция для получения истории активности
 */
const activityApi = {
  async getActivity(organizationId, page = 1, pageSize = 20) {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    // Добавляем таймаут для мобильных сетей (10 секунд)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const response = await fetch(`/api/organizations/${organizationId}/activity?${params}`, {
        credentials: 'include',
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Сессия истекла');
        }
        if (response.status === 403) {
          throw new Error('Нет доступа');
        }
        throw new Error('Не удалось загрузить историю');
      }
      return response.json();
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        throw new Error('Таймаут загрузки');
      }
      throw err;
    }
  },
};

/**
 * Маппинг типов действий на иконки и цвета
 */
const actionConfig = {
  created: { icon: IconPlus, color: 'green', label: 'Создано' },
  updated: { icon: IconPencil, color: 'blue', label: 'Обновлено' },
  deleted: { icon: IconTrash, color: 'red', label: 'Удалено' },
  synced: { icon: IconRefresh, color: 'cyan', label: 'Синхронизация' },
  status_changed: { icon: IconArrowRight, color: 'violet', label: 'Смена статуса' },
  stage_changed: { icon: IconArrowRight, color: 'orange', label: 'Смена этапа' },
  // Этапы воронки сотрудничества
  cooperation_stage_changed: { icon: IconArrowRight, color: 'green', label: 'Смена этапа' },
  cooperation_stage_force_changed: { icon: IconAlertTriangle, color: 'orange', label: 'Принудительно' },
  assigned: { icon: IconUserPlus, color: 'teal', label: 'Назначение' },
  contact_added: { icon: IconUsers, color: 'green', label: 'Контакт добавлен' },
  contact_updated: { icon: IconUsers, color: 'blue', label: 'Контакт изменён' },
  contact_deleted: { icon: IconUsers, color: 'red', label: 'Контакт удалён' },
  meeting_added: { icon: IconCalendar, color: 'green', label: 'Встреча добавлена' },
  meeting_updated: { icon: IconCalendar, color: 'blue', label: 'Встреча изменена' },
  meeting_deleted: { icon: IconCalendar, color: 'red', label: 'Встреча удалена' },
  call_added: { icon: IconPhone, color: 'green', label: 'Звонок добавлен' },
  call_updated: { icon: IconPhone, color: 'blue', label: 'Звонок изменён' },
  call_deleted: { icon: IconPhone, color: 'red', label: 'Звонок удалён' },
  // Группы компаний
  company_group_created: { icon: IconBuildingCommunity, color: 'indigo', label: 'Группа создана' },
  member_added: { icon: IconBuilding, color: 'green', label: 'В группу' },
  member_removed: { icon: IconBuilding, color: 'red', label: 'Из группы' },
};

/**
 * Форматирование даты/времени
 */
const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;

  // Если меньше минуты
  if (diff < 60 * 1000) {
    return 'Только что';
  }

  // Если меньше часа
  if (diff < 60 * 60 * 1000) {
    const minutes = Math.floor(diff / (60 * 1000));
    return `${minutes} мин. назад`;
  }

  // Если сегодня
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) {
    return `Сегодня в ${date.toLocaleString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Moscow',
    })}`;
  }

  // Если вчера
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return `Вчера в ${date.toLocaleString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Moscow',
    })}`;
  }

  // Иначе полная дата
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow',
  });
};

/**
 * Компонент отображения одного события
 */
const ActivityItem = ({ activity }) => {
  const config = actionConfig[activity.action_type] || {
    icon: IconHistory,
    color: 'gray',
    label: activity.action_type,
  };
  const Icon = config.icon;

  const isSystem = !activity.user_id;
  const userName = isSystem
    ? 'Система'
    : activity.user?.full_name || activity.user?.email || 'Пользователь';

  return (
    <Group
      align="flex-start"
      gap="sm"
      py="xs"
      style={{
        borderBottom: `1px solid ${tokens.colors.neutral[2]}`,
      }}
    >
      {/* Аватар или иконка системы */}
      {isSystem ? (
        <Avatar size="sm" radius="xl" color="gray">
          <IconRobot size={16} />
        </Avatar>
      ) : (
        <Avatar size="sm" radius="xl" color="blue">
          {(activity.user?.full_name?.[0] || activity.user?.email?.[0] || 'U').toUpperCase()}
        </Avatar>
      )}

      {/* Контент события */}
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" wrap="nowrap" align="center">
          <Text size="xs" fw={500} truncate>
            {userName}
          </Text>
          <Badge
            size="xs"
            variant="filled"
            radius="xl"
            color={config.color}
            leftSection={<Icon size={10} />}
          >
            {config.label}
          </Badge>
        </Group>

        <Text size="sm" c="dimmed" style={{ marginTop: 2, whiteSpace: 'pre-line' }}>
          {activity.description}
        </Text>

        <Text size="xs" c="dimmed" style={{ marginTop: 4 }}>
          {formatDateTime(activity.created_at)}
        </Text>
      </Box>
    </Group>
  );
};

/**
 * Основной компонент виджета
 */
const ActivityWidget = ({ organization, refreshKey = 0 }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);

  // Загрузка истории активности
  const loadActivity = useCallback(async () => {
    if (!organization?.id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await activityApi.getActivity(organization.id);
      setActivities(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to load activity:', err);
      setError('Не удалось загрузить историю');
      setActivities([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [organization?.id]);

  // Загрузка при монтировании и при изменении refreshKey
  useEffect(() => {
    loadActivity();
  }, [loadActivity, refreshKey]);

  return (
    <BaseWidget
      title="История изменений"
      icon={IconHistory}
      count={total}
      collapsible={true}
      defaultExpanded={activities.length > 0 || error}
    >
      {loading ? (
        <Center py="lg">
          <Loader size="sm" />
        </Center>
      ) : error ? (
        <Box py="lg" style={{ textAlign: 'center' }}>
          <IconHistory
            size={40}
            style={{ color: tokens.colors.semantic.error.primary, marginBottom: '8px' }}
          />
          <Text size="sm" c="red">
            {error}
          </Text>
          <Text
            size="xs"
            c={tokens.colors.brand.primary}
            mt="sm"
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={loadActivity}
          >
            Повторить загрузку
          </Text>
        </Box>
      ) : activities.length > 0 ? (
        <ScrollArea.Autosize mah={400} offsetScrollbars>
          <Stack gap={0}>
            {activities.map((activity) => (
              <ActivityItem key={activity.id} activity={activity} />
            ))}
          </Stack>
        </ScrollArea.Autosize>
      ) : (
        <Box py="lg" style={{ textAlign: 'center' }}>
          <IconHistory
            size={40}
            style={{ color: tokens.colors.neutral[4], marginBottom: '8px' }}
          />
          <Text size="sm" c="dimmed">
            История пуста
          </Text>
          <Text size="xs" c="dimmed" mt={4}>
            Здесь будут отображаться все изменения
          </Text>
        </Box>
      )}
    </BaseWidget>
  );
};

export default ActivityWidget;
